import {
    computed,
    reactive,
    toRefs,
    watch,
    ref,
    type Ref,
    onUnmounted,
} from 'vue';
import { useFilters } from './useFilters';
import {
    useGet,
    usePost,
    useToaster,
    usePut,
    useDelete,
} from '@foodics/ui-common';
import { omitBy, merge } from 'lodash-es';
import { useSort } from './useSort';
import type {
    BankAccount,
    SadadBill,
    SadadBillStatus,
    SadadBillPayment,
    SadadBiller,
    SadadBillType,
    SadadBillPaymentStatus,
} from '@/types/models';
import { createSharedComposable, createEventHook } from '@vueuse/core';
import { sadadApi } from '@/api/sadad';
import type {
    SadadBillPaymentDetailsRequest,
    SadadBillPaymentsRequest,
    AddSadadBillToFavoritesRequest,
    InquiredSadadBill,
    InquireSadadBillRequest,
    ListSadadBillersRequest,
    PaySadadBillRequest,
    EditBillRequest,
    ListSadadBillersCategoriesRequest,
    SadadBillDetailsRequest,
    SadadBillsRequest,
    RefreshSadadBillRequest,
} from '@/api/sadad';
import { parseError } from '@/utils/errors';
import { usePagination } from './usePagination';
import { useLocale } from './locale';
import {
    sadadBillPaymentMethodEnum,
    sadadBillPaymentTypeEnum,
    sadadBillStatusEnum,
    sadadBillTypeEnum,
} from '@/enums/sadad';

// Re-export bill refresh manager
export * from './billRefreshManager';
import { onBillRefreshed } from './billRefreshManager';

export type PaymentType = 'bill' | 'oneTimePayment' | 'governmentPayment';

export const SadadBillStatusEnum = {
    PAID: 1,
    PARTIALLY_PAID: 2,
    OVERDUE: 3,
    UNPAID: 4,
    DEACTIVATED: 5,
} as const satisfies Record<string, SadadBillStatus>;

export type BillStatus = 1 | 2;
export type SadadPaymentMode = 'bill' | 'oneTimePayment' | 'governmentPayment';

export const SadadBillPaymentStatusEnum = {
    PENDING: 1,
    PROCESSED: 2,
    FAILED: 3,
    PAID: 4,
} as const satisfies Record<string, SadadBillPaymentStatus>;

export const unpaidStatuses: SadadBillStatus[] = [
    sadadBillStatusEnum.UNPAID,
    sadadBillStatusEnum.PARTIAL_PAID,
];

export type SadadBillFilters = {
    status?: string;
};

export type SadadBillsSort =
    | 'due_date'
    | '-due_date'
    | 'biller_name'
    | '-biller_name';

export type SadadBillPaymentsSort =
    | 'datetime'
    | '-datetime'
    | 'amount'
    | '-amount'
    | 'biller_name'
    | '-biller_name';

const { on: onBillUpdated, trigger: triggerBillUpdated } = createEventHook<
    Partial<SadadBill> & { id: string; name: string }
>();

const { on: onBillAdded, trigger: triggerBillAdded } =
    createEventHook<SadadBill>();
const { on: onBillDeleted, trigger: triggerBillDeleted } =
    createEventHook<string>();
const { on: onBillPaymentAdded, trigger: triggerBillPaymentAdded } =
    createEventHook<SadadBillPayment>();

// Connect bill refresh manager to bill update events
onBillRefreshed(triggerBillUpdated);

export const useSadadBills = createSharedComposable(() => {
    const { setFilter, appliedFilters } = useFilters<SadadBillFilters>({
        ignoreQueryParams: true,
        initialValues: {
            status: undefined,
        },
    });

    const { sort, setSort, getSortedBy } = useSort<SadadBillsSort>({
        ignoreQueryParams: true,
    });

    const { data, isLoading, error, abort, execute } = useGet<
        SadadBillsRequest['response']
    >(
        sadadApi.list,
        computed(() => ({
            params: {
                ...omitBy(
                    {
                        sort: sort.value,
                        filter: omitBy(
                            {
                                status: appliedFilters.value?.status,
                            },
                            (value) => !value,
                        ),
                    },
                    (value) => !value,
                ),
            },
            disableAutoExecute: true,
        })),
    );

    // Abort previous request and execute new one when filters change
    watch(
        [appliedFilters, sort],
        () => {
            abort?.();
            execute();
        },
        { deep: true, immediate: true },
    );

    onBillUpdated((bill) => {
        if (!data.value?.data) return;
        const billIndex = data.value.data.findIndex((b) => b.id === bill.id);
        data.value.data[billIndex] = merge(
            {},
            data.value.data[billIndex],
            bill,
        );
    });

    onBillAdded((bill) => {
        if (!data.value?.data) return;
        data.value.data.push(bill);
    });

    onBillDeleted((billId) => {
        if (!data.value?.data) return;
        data.value.data = data.value.data.filter((b) => b.id !== billId);
    });

    const hasBillsAdded = computed(() => {
        return Boolean(data.value?.data?.length);
    });

    return {
        hasBillsAdded,
        bills: computed(() => data.value?.data || []),
        isLoading,
        error,
        setFilter,
        appliedFilters,
        sort,
        setSort,
        getSortedBy,
    };
});

export const useUpdateBill = createSharedComposable((bill: SadadBill) => {
    const { addToast } = useToaster();
    const { t } = useLocale();
    const editValues = reactive({
        name: bill.name,
    });
    const {
        execute: updateBill,
        isLoading: isUpdating,
        error,
    } = usePut<EditBillRequest['payload'], EditBillRequest['response']>(
        sadadApi.edit(bill.id),
    );
    async function update() {
        try {
            const { data, error } = await updateBill({
                name: editValues.name,
            });
            if (error || !data) throw error;
            addToast({
                title: t('sadad.edit.success'),
                variant: 'success',
            });

            triggerBillUpdated(data.data);
        } catch (error) {
            addToast({
                title: parseError(error, t('sadad.edit.error')),
                variant: 'error',
            });
        }
    }

    return {
        ...toRefs(editValues),
        update,
        isUpdating,
        error,
    };
});

export const useDeleteBill = (billId: string) => {
    const { addToast } = useToaster();
    const { t } = useLocale();

    const {
        execute,
        isLoading: isDeleting,
        error,
    } = useDelete(sadadApi.delete(billId), { responseType: 'blob' });

    async function deleteBill() {
        try {
            const { error } = await execute();
            if (error) throw error;

            addToast({
                title: t('sadad.delete.success'),
                variant: 'success',
            });

            triggerBillDeleted(billId);
            return true;
        } catch (error) {
            addToast({
                title: parseError(error, t('sadad.delete.error')),
                variant: 'error',
            });
            return false;
        }
    }

    return {
        deleteBill,
        isDeleting,
        error,
    };
};

export const useSadadBillers = createSharedComposable(() => {
    const { setFilter, appliedFilters } = useFilters<{
        category_id?: string;
        search: string;
    }>({
        ignoreQueryParams: true,
        initialValues: { search: '' },
    });

    const { data, isLoading, error } = useGet<
        ListSadadBillersRequest['response']
    >(
        sadadApi.listBillers,
        computed(() => ({
            params: {
                include: 'category',
                ...omitBy(
                    {
                        filter: {
                            ...omitBy(appliedFilters.value, (value) => !value),
                        },
                    },
                    (value) => !value,
                ),
            },
        })),
    );

    return {
        billers: computed(() => data.value?.data || []),
        isLoading,
        error,
        setFilter,
        appliedFilters,
    };
});

export const useSadadBillerCategories = createSharedComposable(() => {
    const { data, isLoading, error } = useGet<
        ListSadadBillersCategoriesRequest['response']
    >(sadadApi.listBillersCategories);

    return {
        categories: computed(() => data.value?.data || []),
        isLoading,
        error,
    };
});

export const useAddSadadBillToFavorites = createSharedComposable(() => {
    const { addToast } = useToaster();
    const { t } = useLocale();
    const { execute, isLoading, error, abort, onData, data } = usePost<
        AddSadadBillToFavoritesRequest['payload'],
        AddSadadBillToFavoritesRequest['response']
    >(sadadApi.addBillToFavorites);

    async function addToFavorites(
        payload: AddSadadBillToFavoritesRequest['payload'],
        options?: { showSuccessToast?: boolean; showErrorToast?: boolean },
    ) {
        try {
            const { data, error } = await execute(payload);
            if (error) throw error;
            if (!data?.data) throw new Error('Something went wrong');

            if (options?.showSuccessToast) {
                addToast({
                    title: t('sadad.new.addToFavoritesSuccess.title'),
                    variant: 'success',
                });
            }

            triggerBillAdded(data.data);

            return data.data;
        } catch (error) {
            if (options?.showErrorToast) {
                addToast({
                    title: parseError(error),
                    variant: 'error',
                });
            }
        }
    }

    return {
        data,
        addToFavorites,
        isLoading,
        error,
        abort,
        onData,
    };
});

export const usePaySadadBill = () => {
    const { addToast } = useToaster();
    const { t } = useLocale();

    const { data, isLoading, error, execute, abort, onData } = usePost<
        PaySadadBillRequest['payload'],
        PaySadadBillRequest['response']
    >(sadadApi.payBill);

    async function pay(
        payload: PaySadadBillRequest['payload'],
        options?: { showToast?: boolean },
    ) {
        try {
            const { data, error } = await execute(payload);
            if (error) throw error;
            if (!data?.data) throw new Error('Something went wrong');

            if (options?.showToast) {
                addToast({
                    title: t('sadad.new.billPaymentSuccess.title'),
                    variant: 'success',
                });
            }

            triggerBillPaymentAdded(data.data);

            return data.data;
        } catch (error) {
            if (options?.showToast) {
                addToast({
                    title: parseError(error),
                    variant: 'error',
                });
            }
        }
    }

    return {
        pay,
        data,
        isLoading,
        error,
        abort,
        onPayFulfilled: onData,
    };
};

export const useAddSadadBill = createSharedComposable(() => {
    const { addToast } = useToaster();

    const billInfo = reactive<{
        biller: SadadBiller | undefined;
        paymentMode: SadadPaymentMode | undefined;
        billType: SadadBillType;
        billNumber: string;
        inquiredBill: InquiredSadadBill | undefined;
        fromBankAccount: BankAccount | undefined;
        amount: string;
        billNickname: string;
    }>({
        biller: undefined,
        paymentMode: undefined,
        billType: sadadBillTypeEnum.POSTPAID,
        billNumber: '',
        inquiredBill: undefined,
        fromBankAccount: undefined,
        amount: '',
        billNickname: '',
    });

    function reset() {
        billInfo.biller = undefined;
        billInfo.paymentMode = undefined;
        billInfo.billType = sadadBillTypeEnum.POSTPAID;
        billInfo.billNumber = '';
        billInfo.inquiredBill = undefined;
        billInfo.fromBankAccount = undefined;
        billInfo.amount = '';
        billInfo.billNickname = '';
    }

    const {
        execute: executeInquireBill,
        isLoading: isInquiringBill,
        error: inquireBillError,
    } = useInquireSadadBill();

    const inquireBill = async () => {
        if (!billInfo.biller?.id || !billInfo.billNumber || !billInfo.billType)
            return;

        const { data } = await executeInquireBill({
            biller_id: billInfo.biller.id,
            bill_type: billInfo.billType,
            billing_account: billInfo.billNumber,
        });

        if (!data?.data) return;

        const bill = data?.data?.[0];
        billInfo.inquiredBill = bill;
        billInfo.amount = bill.due_amount.value.toString();
    };

    // reset bill info when biller is changed
    watch(
        () => billInfo.biller,
        () => {
            billInfo.billNumber = '';
        },
    );

    // reset bill info when bill number is changed
    watch(
        () => billInfo.billNumber,
        () => {
            billInfo.inquiredBill = undefined;
            inquireBillError.value = null;
            billInfo.amount = '';
            billInfo.billNickname = '';
            billInfo.fromBankAccount = undefined;
        },
    );

    watch(
        () => billInfo.billType,
        () => {
            billInfo.billNumber = '';
        },
    );

    // pay sadad bill
    const {
        pay: paySadadBill,
        onPayFulfilled,
        isLoading: isPaying,
        error: payError,
        data: paidBill,
    } = usePaySadadBill();

    // add to favorites
    const {
        addToFavorites,
        data: addedToFavoritesBill,
        isLoading: isAddingToFavorites,
        error: addToFavoritesError,
        onData: onAddToFavoritesFulfilled,
    } = useAddSadadBillToFavorites();

    async function submit(otp: string) {
        try {
            if (!billInfo.biller) throw new Error('Biller is required');
            if (!billInfo.fromBankAccount)
                throw new Error('Bank account is required');
            if (!billInfo.billNumber)
                throw new Error('Bill number is required');

            const payPromise = paySadadBill(
                {
                    biller_id: billInfo.biller.id,
                    bill_number: billInfo.billNumber,
                    source_bank_account_id: billInfo.fromBankAccount.id,
                    payment_method: sadadBillPaymentMethodEnum.ACTDEB,
                    payment_type: sadadBillPaymentTypeEnum.POST,
                    amount: billInfo.amount,
                    otp_token: otp,
                    currency: 'SAR',
                },
                { showToast: false },
            );

            const actionsPromises: Promise<any>[] = [payPromise];

            if (billInfo.paymentMode === 'bill') {
                actionsPromises.push(
                    addToFavorites(
                        {
                            biller_id: billInfo.biller.id,
                            type: billInfo.billType,
                            number: billInfo.billNumber,
                            name: billInfo.billNickname,
                        },
                        { showSuccessToast: false, showErrorToast: true },
                    ),
                );
            }

            await Promise.all(actionsPromises);
        } catch (error) {
            addToast({
                title: parseError(error),
                variant: 'error',
            });
        }
    }

    return {
        ...toRefs(billInfo),
        isInquiringBill,
        inquireBillError,
        inquireBill,
        reset,
        // Pay bill
        isPaying,
        payError,
        onPayFulfilled,
        paidBill,

        // Add to favorites
        isAddingToFavorites,
        addToFavoritesError,
        onAddToFavoritesFulfilled,
        addedToFavoritesBill,

        //submitting
        isSubmitting: computed(
            () => isPaying.value || isAddingToFavorites.value,
        ),
        submit,
    };
});

export const useSadadBill = (billId: string) => {
    const { data, isLoading, error } = useGet<
        SadadBillDetailsRequest['response']
    >(sadadApi.details(billId), {
        params: {
            include: ['biller', 'creator'],
        },
    });
    onBillUpdated((bill) => {
        if (bill.id !== billId || !data.value?.data) return;
        data.value.data = merge({}, data.value.data, bill);
    });

    return {
        bill: computed(() => data.value?.data),
        error,
        isLoading,
    };
};

export const useSadadBillPayments = createSharedComposable(() => {
    const payments = ref<SadadBillPayment[]>([]);
    const { sort, setSort, getSortedBy } = useSort<SadadBillPaymentsSort>({
        ignoreQueryParams: true,
    });

    const {
        items,
        isLoading,
        hasNextPage,
        fetchNextPage,
        hasFetchedInitialPage,
        resetPagination,
    } = usePagination<SadadBillPaymentsRequest>(sadadApi.payments, {
        fetchOptions: computed(() => ({
            params: {
                include: [
                    'creator',
                    'biller',
                    'sourceBankAccount.bank',
                    'transaction',
                ],
                sort: sort.value,
            },
        })),
    });

    watch(
        items,
        () => {
            payments.value.push(...items.value);
        },
        { immediate: true },
    );

    watch(sort, () => {
        payments.value = [];
        resetPagination();
    });

    onBillDeleted(() => {
        payments.value = [];
        resetPagination();
    });

    onBillPaymentAdded((newPayment) => {
        if (payments.value.some((p) => p.id === newPayment.id)) return;
        payments.value.unshift(newPayment);
    });

    return {
        payments: computed(() => payments.value),
        isLoading,
        hasNextPage,
        fetchNextPage,
        hasFetchedInitialPage,
        resetPagination,
        setSort,
        getSortedBy,
    };
});

export const useSadadBillPayment = (paymentId: string) => {
    const { data, isLoading, error } = useGet<
        SadadBillPaymentDetailsRequest['response']
    >(sadadApi.paymentDetails(paymentId), {
        params: {
            include: [
                'creator',
                'biller',
                'sourceBankAccount.bank',
                'transaction',
            ],
        },
    });

    return {
        payment: computed(() => data.value?.data),
        error,
        isLoading,
    };
};
export function useInquireSadadBill() {
    const { data, isLoading, error, execute, abort } = usePost<
        InquireSadadBillRequest['payload'],
        InquireSadadBillRequest['response']
    >(sadadApi.inquireBill);

    return {
        execute,
        data,
        isLoading,
        error,
        abort,
    };
}

export function resolveSadadBillPaymentPartyName(payment: SadadBillPayment) {
    return payment.bill_name || payment.biller.name;
}

const BILL_REFRESH_MAP = new Map<string, Ref<boolean>>();
export function useRefreshSadadBill(billId: string) {
    const { data, isLoading, error, execute, abort, onData } = usePost<
        RefreshSadadBillRequest['payload'],
        RefreshSadadBillRequest['response']
    >(sadadApi.refreshBill(billId));

    BILL_REFRESH_MAP.set(billId, isLoading);

    onData((refreshedBill) => {
        if (!refreshedBill?.data) return;
        triggerBillUpdated(refreshedBill.data);
    });

    onUnmounted(() => {
        BILL_REFRESH_MAP.delete(billId);
    });

    return {
        refresh: execute,
        data,
        isLoading,
        error,
        abort,
        onBillRefreshed: onData,
    };
}

export function isRefreshingBill(billId: string) {
    return BILL_REFRESH_MAP.get(billId)?.value || false;
}
